import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  TrendingUpIcon,
  TrendingDownIcon,
  UsersIcon,
  BuildingIcon,
  CreditCardIcon,
  ActivityIcon,
  DownloadIcon,
  RefreshCwIcon,
  CalendarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  MinusIcon,
  Banknote as BanknoteIcon,
  PercentIcon,
  BarChart3Icon
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UltraAnalytics = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30days'); // 7days, 30days, 90days, 12months
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('ultra_admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Fetch companies and users
      const [companiesRes, usersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ultra-admin/tenants`, config),
        axios.get(`${BACKEND_URL}/api/ultra-admin/users`, config)
      ]);

      setCompanies(companiesRes.data.tenants);
      setUsers(usersRes.data.users);

      // Calculate analytics
      const analyticsData = calculateAnalytics(companiesRes.data.tenants, usersRes.data.users);
      setAnalytics(analyticsData);

    } catch (error) {
      toast.error('Analitik verileri yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const calculateAnalytics = (companies, users) => {
    // Current period stats
    const totalCompanies = companies.length;
    const activeCompanies = companies.filter(c => c.status === 'active').length;
    const trialCompanies = companies.filter(c => c.status === 'trial').length;
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.is_active).length;
    
    // Revenue calculations
    const mrr = companies.reduce((sum, c) => sum + (c.monthly_fee || 0), 0);
    const arr = mrr * 12;
    const avgRevenuePerCompany = totalCompanies > 0 ? mrr / totalCompanies : 0;

    // User stats
    const avgUsersPerCompany = totalCompanies > 0 ? totalUsers / totalCompanies : 0;

    // Plan distribution
    const planDistribution = companies.reduce((acc, c) => {
      const plan = c.subscription_plan;
      acc[plan] = (acc[plan] || 0) + 1;
      return acc;
    }, {});

    // Growth simulation (mock data - replace with real historical data)
    const growthData = generateGrowthData(timeRange);
    const revenueData = generateRevenueData(timeRange);
    const userActivityData = generateUserActivityData();

    // Previous period comparison (mock)
    const previousMrr = mrr * 0.85; // 15% growth
    const previousUsers = totalUsers * 0.9; // 10% growth
    const previousCompanies = totalCompanies * 0.88; // 12% growth

    return {
      current: {
        totalCompanies,
        activeCompanies,
        trialCompanies,
        totalUsers,
        activeUsers,
        mrr,
        arr,
        avgRevenuePerCompany,
        avgUsersPerCompany
      },
      growth: {
        companiesGrowth: ((totalCompanies - previousCompanies) / previousCompanies * 100).toFixed(1),
        usersGrowth: ((totalUsers - previousUsers) / previousUsers * 100).toFixed(1),
        revenueGrowth: ((mrr - previousMrr) / previousMrr * 100).toFixed(1)
      },
      planDistribution,
      growthData,
      revenueData,
      userActivityData,
      topCompanies: companies.sort((a, b) => b.user_count - a.user_count).slice(0, 5)
    };
  };

  const generateGrowthData = (range) => {
    const days = range === '7days' ? 7 : range === '30days' ? 30 : range === '90days' ? 90 : 365;
    const data = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' }),
        companies: Math.floor(Math.random() * 3) + companies.length - 2,
        users: Math.floor(Math.random() * 5) + users.length - 3
      });
    }
    
    return data;
  };

  const generateRevenueData = (range) => {
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    const data = [];
    
    for (let i = 0; i < 6; i++) {
      const monthIndex = (new Date().getMonth() - 5 + i + 12) % 12;
      data.push({
        month: months[monthIndex],
        revenue: Math.floor(Math.random() * 200) + 100,
        transactions: Math.floor(Math.random() * 15) + 5
      });
    }
    
    return data;
  };

  const generateUserActivityData = () => {
    return [
      { name: 'Aktif', value: users.filter(u => u.is_active).length, color: '#10b981' },
      { name: 'Pasif', value: users.filter(u => !u.is_active).length, color: '#ef4444' }
    ];
  };

  const getGrowthIcon = (growth) => {
    const value = parseFloat(growth);
    if (value > 0) return <ArrowUpIcon className="w-4 h-4" />;
    if (value < 0) return <ArrowDownIcon className="w-4 h-4" />;
    return <MinusIcon className="w-4 h-4" />;
  };

  const getGrowthColor = (growth) => {
    const value = parseFloat(growth);
    if (value > 0) return 'text-green-600';
    if (value < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <RefreshCwIcon className="w-12 h-12 text-purple-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-600">Analitik verileri yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Analitik & Raporlar</h2>
            <p className="text-gray-600 mt-1">Detaylı performans metrikleri ve büyüme analizleri</p>
          </div>
          <div className="flex space-x-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="7days">📅 Son 7 Gün</option>
              <option value="30days">📅 Son 30 Gün</option>
              <option value="90days">📅 Son 90 Gün</option>
              <option value="12months">📅 Son 12 Ay</option>
            </select>
            <Button variant="outline" onClick={fetchAnalytics}>
              <RefreshCwIcon className="w-4 h-4 mr-2" />
              Yenile
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700">
              <DownloadIcon className="w-4 h-4 mr-2" />
              Rapor İndir
            </Button>
          </div>
        </div>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* MRR Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
              <BanknoteIcon className="w-6 h-6 text-white" />
            </div>
            <div className={`flex items-center space-x-1 ${getGrowthColor(analytics.growth.revenueGrowth)}`}>
              {getGrowthIcon(analytics.growth.revenueGrowth)}
              <span className="text-sm font-semibold">{Math.abs(analytics.growth.revenueGrowth)}%</span>
            </div>
          </div>
          <p className="text-gray-600 text-sm font-medium">Aylık Tekrar Eden Gelir</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">₺{analytics.current.mrr.toFixed(0)}</p>
          <p className="text-gray-500 text-xs mt-2">ARR: ₺{analytics.current.arr.toFixed(0)}</p>
        </Card>

        {/* Companies Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <BuildingIcon className="w-6 h-6 text-white" />
            </div>
            <div className={`flex items-center space-x-1 ${getGrowthColor(analytics.growth.companiesGrowth)}`}>
              {getGrowthIcon(analytics.growth.companiesGrowth)}
              <span className="text-sm font-semibold">{Math.abs(analytics.growth.companiesGrowth)}%</span>
            </div>
          </div>
          <p className="text-gray-600 text-sm font-medium">Toplam Kiracı</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.current.totalCompanies}</p>
          <p className="text-gray-500 text-xs mt-2">{analytics.current.activeCompanies} aktif</p>
        </Card>

        {/* Users Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
              <UsersIcon className="w-6 h-6 text-white" />
            </div>
            <div className={`flex items-center space-x-1 ${getGrowthColor(analytics.growth.usersGrowth)}`}>
              {getGrowthIcon(analytics.growth.usersGrowth)}
              <span className="text-sm font-semibold">{Math.abs(analytics.growth.usersGrowth)}%</span>
            </div>
          </div>
          <p className="text-gray-600 text-sm font-medium">Toplam Kullanıcı</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{analytics.current.totalUsers}</p>
          <p className="text-gray-500 text-xs mt-2">{analytics.current.activeUsers} aktif</p>
        </Card>

        {/* ARPU Card */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
              <TrendingUpIcon className="w-6 h-6 text-white" />
            </div>
          </div>
          <p className="text-gray-600 text-sm font-medium">Kiracı Başına Ort. Gelir</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">₺{analytics.current.avgRevenuePerCompany.toFixed(0)}</p>
          <p className="text-gray-500 text-xs mt-2">ARPU (Average Revenue Per User)</p>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Growth Chart */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Büyüme Trendi</h3>
            <BarChart3Icon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {/* Simple bar chart */}
            {analytics.growthData.slice(-10).map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{item.date}</span>
                  <span className="font-medium text-gray-900">{item.companies} kiracı</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-purple-600 h-2 rounded-full transition-all"
                    style={{ width: `${(item.companies / Math.max(...analytics.growthData.map(d => d.companies))) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Revenue Chart */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Gelir Trendi</h3>
            <BanknoteIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-4">
            {analytics.revenueData.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{item.month}</span>
                  <div className="flex items-center space-x-4">
                    <span className="text-gray-500 text-xs">{item.transactions} işlem</span>
                    <span className="font-medium text-gray-900">₺{item.revenue}</span>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 h-2 rounded-full transition-all"
                    style={{ width: `${(item.revenue / Math.max(...analytics.revenueData.map(d => d.revenue))) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Plan Distribution & Top Companies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Plan Distribution */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Plan Dağılımı</h3>
          <div className="space-y-4">
            {Object.entries(analytics.planDistribution).map(([plan, count]) => {
              const percentage = (count / analytics.current.totalCompanies * 100).toFixed(0);
              const planNames = {
                free_trial: { name: 'Free Trial', color: 'from-gray-500 to-gray-600', emoji: '🆓' },
                starter: { name: 'Starter', color: 'from-blue-500 to-blue-600', emoji: '🥉' },
                professional: { name: 'Professional', color: 'from-purple-500 to-purple-600', emoji: '🥈' },
                enterprise: { name: 'Enterprise', color: 'from-orange-500 to-orange-600', emoji: '🥇' }
              };
              const planInfo = planNames[plan] || { name: plan, color: 'from-gray-400 to-gray-500', emoji: '📦' };

              return (
                <div key={plan}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <span>{planInfo.emoji}</span>
                      <span className="text-sm font-medium text-gray-700">{planInfo.name}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-semibold text-gray-900">{count} kiracı</span>
                      <Badge className="bg-purple-100 text-purple-700">{percentage}%</Badge>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`bg-gradient-to-r ${planInfo.color} h-3 rounded-full transition-all`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Top Companies */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">En Çok Kullanıcılı Kiracılar</h3>
          <div className="space-y-4">
            {analytics.topCompanies.map((company, index) => (
              <div key={company.id} className="flex items-center space-x-4">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 text-white font-bold text-sm">
                  {index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{company.company_name}</p>
                  <p className="text-xs text-gray-500">
                    {company.subscription_plan} • {company.status}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-900">{company.user_count}</p>
                  <p className="text-xs text-gray-500">kullanıcı</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Additional Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <UsersIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Ortalama Kullanıcı/Kiracı</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.current.avgUsersPerCompany.toFixed(1)}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <ActivityIcon className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Aktif Kullanıcı Oranı</p>
              <p className="text-2xl font-bold text-gray-900">
                {((analytics.current.activeUsers / analytics.current.totalUsers) * 100).toFixed(0)}%
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <CalendarIcon className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-gray-600 text-sm">Deneme Kiracıları</p>
              <p className="text-2xl font-bold text-gray-900">{analytics.current.trialCompanies}</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default UltraAnalytics;
